// heic2any lazy loaded below (341 KB gzipped - only load when HEIC file uploaded)

import { UPLOAD_CAPABILITY_CONTRACT, type UploadCapability } from '@repo/common';
import { IMAGE_UPLOAD_SUPPORTED_EXTENSIONS, IMAGE_UPLOAD_SUPPORTED_MIME_TYPES } from '@repo/ui/constants/image-upload';

function getImageUploadCapability(): UploadCapability {
  const capability = UPLOAD_CAPABILITY_CONTRACT.find(candidate => candidate.resourceType === 'IMAGE');
  if (!capability) {
    throw new Error('Image upload capability is not configured');
  }
  return capability;
}

const IMAGE_UPLOAD_CAPABILITY = getImageUploadCapability();
const MAX_IMAGE_HEIGHT = 1280;
const IMAGE_ENCODING_QUALITY = 0.8;

function getFileExtension(fileName: string): string | undefined {
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : undefined;
  return extension && extension.length > 0 ? extension : undefined;
}

const changeFileExtension = (fileName: string, extension: string) => {
  if (fileName.includes('.')) {
    const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));

    return `${nameWithoutExtension}.${extension}`;
  }

  return `${fileName}.${extension}`;
};

async function convertWithCanvas(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Could not decode image'));
      element.src = url;
    });
    const scale = Math.min(1, MAX_IMAGE_HEIGHT / image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to get 2D context');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        encoded => (encoded ? resolve(encoded) : reject(new Error('Could not encode image'))),
        'image/webp',
        IMAGE_ENCODING_QUALITY,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function encodeImage(blob: Blob): Promise<Blob> {
  if (typeof window.createImageBitmap !== 'function' || typeof window.OffscreenCanvas !== 'function') {
    return convertWithCanvas(blob);
  }
  try {
    const bitmap = await window.createImageBitmap(blob);
    try {
      const scale = Math.min(1, MAX_IMAGE_HEIGHT / bitmap.height);
      const canvas = new window.OffscreenCanvas(
        Math.max(1, Math.round(bitmap.width * scale)),
        Math.max(1, Math.round(bitmap.height * scale)),
      );
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Failed to get 2D context');
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      return await canvas.convertToBlob({ type: 'image/webp', quality: IMAGE_ENCODING_QUALITY });
    } finally {
      bitmap.close();
    }
  } catch {
    // Some browsers expose these APIs but cannot decode or encode this image with them.
    return convertWithCanvas(blob);
  }
}

function getImageFormat(file: File): string {
  const fileExtension = getFileExtension(file.name);
  if (fileExtension && IMAGE_UPLOAD_SUPPORTED_EXTENSIONS.includes(fileExtension)) {
    return fileExtension;
  }

  const mimeType = file.type.toLowerCase();
  if (IMAGE_UPLOAD_SUPPORTED_MIME_TYPES.includes(mimeType)) {
    return mimeType.slice('image/'.length);
  }

  throw new Error(`Unsupported image format: ${file.name}`);
}

function imageRequiresUploadTransform(file: File): boolean {
  const extension = getFileExtension(file.name);
  const mimeType = file.type.toLowerCase();
  const selectionFormat = IMAGE_UPLOAD_CAPABILITY.selectionFormats.find(
    format => (extension !== undefined && format.extensions.includes(extension)) || format.mimeTypes.includes(mimeType),
  );

  return selectionFormat?.transform !== null && selectionFormat?.transform !== undefined;
}

export const convertToWebp = async (file: File): Promise<File> => {
  const imageFormat = getImageFormat(file);

  if (imageFormat === 'webp') {
    return file;
  }

  let imageBuffer: ArrayBuffer;

  if (imageFormat === 'heic' || imageFormat === 'heif') {
    // Lazy load heic2any library (341 KB gzipped) only when HEIC file is uploaded
    const { default: heic2any } = await import('heic2any');
    const heicBuffer = await file.arrayBuffer();
    const jpegBlob = await heic2any({ blob: new Blob([heicBuffer]), toType: 'image/jpeg' });
    if (Array.isArray(jpegBlob)) {
      throw new Error('Expected one image from HEIC conversion');
    }
    imageBuffer = await jpegBlob.arrayBuffer();
  } else {
    imageBuffer = await file.arrayBuffer();
  }

  const blob = await encodeImage(new Blob([imageBuffer]));
  // Canvas falls back to PNG on browsers without a WebP encoder.
  const extension = blob.type === 'image/webp' ? 'webp' : 'png';
  return new File([blob], changeFileExtension(file.name, extension), { type: blob.type });
};

export async function prepareImageForUpload(file: File): Promise<File> {
  return imageRequiresUploadTransform(file) ? convertToWebp(file) : file;
}
